import { autoinject, transient, computedFrom, useView } from "aurelia-framework";
import { NumberUtilities } from "../../../../../utilities/number-utilities";
import * as Enumerable from "linq";
import { FbrItemOutcomeStepEnum, FbrQuestionnaireTypesEnum, VesselFbrPerformanceItem } from "../../../../../models/fbrs/fbr.models";
import { ChartDataModel } from "../../../../../models/components/chart.models";
import { FbrService } from "../../../../../services/fbr/fbr.service";
import { Reflection } from "../../../../../utilities/reflection";

@autoinject()
@transient()
@useView('views/pages/modules/vessels/steps/vessel-fbr-performance.html')
export class VesselFbrPerformance {

    public isDry: boolean = true;
    public items: VesselFbrPerformanceItem[] = [];
    public dryBerth: VesselFbrPerformanceItem[] = [];
    public terminal: VesselFbrPerformanceItem[] = [];
    public vesselId: number;
    public chartYears: { [index: number]: number } = {
        0: 5,
        1: 5,
        2: 5,
        3: 5,
        4: 5
    };
    public chartSubCategories: string[] = [];
    public chartCategories: Enumerable.IGrouping<string, VesselFbrPerformanceItem>[] = [];
    public chartItemSource = [
        { name: "last year", year: 1 },
        { name: "last 2 years", year: 2 },
        { name: "last 3 years", year: 3 },
        { name: "last 4 years", year: 4 },
        { name: "last 5 years", year: 5 }
    ];

    @computedFrom(Reflection.getPropertyPath<VesselFbrPerformance>(z => z.chartCategories),
    Reflection.getPropertyPath<VesselFbrPerformance>(z => z.chartCategories.length),
    Reflection.getPropertyPath<VesselFbrPerformance>(z => z.chartYears))
    get chartCategoryCounts(): { [category: string]: number } {

        const chartCategoryCounts = Enumerable.from(this.chartCategories).select(x => {
            const index = this.chartCategories.indexOf(x) + 1;
            const today = new Date();
            const dateToStr = `${today.getFullYear() - this.chartYears[index]}-${NumberUtilities.pad2(today.getMonth() + 1)}-${NumberUtilities.pad2(today.getDate())}T00:00:00Z`;
            const dateFrom = new Date(dateToStr);
            return {
                category: x.key(),
                count: x.where(x => new Date(x.date) >= dateFrom).select(x => x.subCategory).distinct().count()
            }
        }).toArray();

        const chartCategoryCountsObj = {};

        chartCategoryCounts.forEach(x => {
            chartCategoryCountsObj[x.category] = x.count;
        });
        return chartCategoryCountsObj;
    }

    @computedFrom(Reflection.getPropertyPath<VesselFbrPerformance>(z => z.chartYears[0]),
    Reflection.getPropertyPath<VesselFbrPerformance>(z => z.items ),
    Reflection.getPropertyPath<VesselFbrPerformance>(z => z.items.length))
    get totalNumberOfFeedbackReports() {
        return Enumerable.from(this.chartData(0)).count();
    }

    @computedFrom(Reflection.getPropertyPath<VesselFbrPerformance>(z => z.chartYears[0]),
    Reflection.getPropertyPath<VesselFbrPerformance>(z => z.items ),
    Reflection.getPropertyPath<VesselFbrPerformance>(z => z.items.length))
    get totalNumberOfFeedbackReportsAllSatisfatory() {
        return Enumerable.from(this.chartData(0)).where(x => x.all(a => a.outcome == FbrItemOutcomeStepEnum.Pass || a.outcome == FbrItemOutcomeStepEnum.NA)).count();
    }

    @computedFrom("totalNumberOfFeedbackReportsAllSatisfatory", "totalNumberOfFeedbackReports")
    get positiveFigure() {

        if (!this.totalNumberOfFeedbackReports || !this.totalNumberOfFeedbackReportsAllSatisfatory) {
            return 0;
        }

        const number = (this.totalNumberOfFeedbackReportsAllSatisfatory /
            this.totalNumberOfFeedbackReports) * 100;

        const result = Number(number).toFixed(0);

        return result;
    }

    @computedFrom("totalNumberOfFeedbackReports", "totalNumberOfFeedbackReportsAllSatisfatory")
    get fbrRadiusChartData() {
        const data: ChartDataModel = {
            chartDataEntries: [],
            labels: null
        };

        const satisfatory = Number((100 * this.totalNumberOfFeedbackReportsAllSatisfatory / Math.max(this.totalNumberOfFeedbackReports, 1)).toFixed(0));
        data.chartDataEntries.push({
            data: satisfatory,
            formatData: satisfatory.toString() + "%",
            label: "Total number of Feedback Reports with  only positive and/or not observed responses",
            color: "#008561"
        });

        const unsatisfatory = 100 - satisfatory;
        data.chartDataEntries.push({
            data: unsatisfatory,
            formatData: unsatisfatory.toString() + "%",
            label: "Total number of Feedback Reports with  negative responses",
            color: "#D0D0D0"
        });

        return data;
    }

    constructor(private fbrService: FbrService) {

    }

    async activate(model) {
        this.vesselId = model.vesselId;
        const response = await this.fbrService.getVesselFbrPerformance(this.vesselId);
        this.dryBerth = response.items.filter(x => x.fbrQuestionerType == FbrQuestionnaireTypesEnum.DryBerthReport || x.fbrQuestionerType == FbrQuestionnaireTypesEnum.LoadingReport);
        this.terminal = response.items.filter(x => x.fbrQuestionerType == FbrQuestionnaireTypesEnum.TankerTerminalReport);

        if (this.terminal.length > 0)
            this.changeReport(false);
        else
            this.changeReport(true);
    }


    getFiltered(i: number) {
        const today = new Date();
        const dateToStr = `${today.getFullYear() - this.chartYears[i]}-${NumberUtilities.pad2(today.getMonth() + 1)}-${NumberUtilities.pad2(today.getDate())}T00:00:00Z`;
        const dateFrom = new Date(dateToStr);
        const itemsQueries = Enumerable.from(this.items).where(x => new Date(x.date) >= dateFrom);
        const items = itemsQueries.groupBy(x => x.fbrId);
        return items.toArray();
    }

    changeReport(isDry: boolean) {
        this.isDry = isDry
        this.items = isDry ? this.dryBerth : this.terminal;
        this.chartCategories = Enumerable.from(this.items).groupBy(x => x.category)
            .orderByDescending(x => x.key().split(' ')[0])
            .thenBy(x => x.key().split(' ')[1])
            .toArray();

        this.chartSubCategories = Enumerable.from(this.items).select(x => x.subCategory).distinct()
            .toArray();
    }

    totalNumberOfFeedbackReportsForChart(index: number) {
        return Enumerable.from(this.chartData(index + 1)).count();
    }

    chartData(number: number) {
        return this.getFiltered(number);
    }

    onFbrRadiusChartOptionsReady($event) {
        const options = $event.detail.options;
        options.innerRadius = "90%";
        options.showBalloon = false;
        options.labelsEnabled = false;
        options.balloonText = "";
        options.labelText = "";
    }

    getGroupedSubCategoriesOutcomes(c: Enumerable.IGrouping<string, VesselFbrPerformanceItem>) {
        return c.groupBy(x => x.outcome).select(x => {
            return {
                outcome: x.key(),
                percentage: x.count() / c.count() * 100
            }
        }).toArray();
    }

    getGroupedSubCategories(c: Enumerable.IGrouping<string, VesselFbrPerformanceItem>, index: number) {
        const today = new Date();
        const dateToStr = `${today.getFullYear() - this.chartYears[index]}-${NumberUtilities.pad2(today.getMonth() + 1)}-${NumberUtilities.pad2(today.getDate())}T00:00:00Z`;
        const dateFrom = new Date(dateToStr);
        return c.where(x => new Date(x.date) >= dateFrom).groupBy(x => x.subCategory).toArray();
    }

    getTooltipText(outcome: number, c: Enumerable.IGrouping<string, VesselFbrPerformanceItem>) {
        return this.getGroupedAnswers(c).filter(x => x.outcome == outcome).map(x => x.answer)[0];
    }

    getGroupedAnswers(c: Enumerable.IGrouping<string, VesselFbrPerformanceItem>) {
        switch (c.key()) {
            case "Operational incidents in":
                return [
                    { answer: "Yes", outcome: FbrItemOutcomeStepEnum.Fail },
                    { answer: "No", outcome: FbrItemOutcomeStepEnum.Pass },
                    { answer: "Not observed", outcome: FbrItemOutcomeStepEnum.NA }
                ]
            case "Operational performance in":
                return [
                    { answer: "Satisfactory", outcome: FbrItemOutcomeStepEnum.Pass },
                    { answer: "Unsatisfactory", outcome: FbrItemOutcomeStepEnum.Fail },
                    { answer: "Not observed", outcome: FbrItemOutcomeStepEnum.NA }
                ]
            case "Management practices performance in":
                return [
                    { answer: "Satisfactory", outcome: FbrItemOutcomeStepEnum.Pass },
                    { answer: "Unsatisfactory", outcome: FbrItemOutcomeStepEnum.Fail },
                    { answer: "Not observed", outcome: FbrItemOutcomeStepEnum.NA }
                ]
        }

        return []
    }
}